import React, { useEffect, useMemo, useState } from "react";
import { Check, ChevronDown, Search, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { detectCountry, findCountry, formatAsYouType, isValidForCountry, listCountries, splitE164, toE164 } from "@/lib/phone";

/**
 * Country-code + national number input. Emits E.164 via onChangeE164(e164, valid).
 * Mirrors the mobile PhoneInput (default country IN, searchable country list).
 */
export function PhoneInput({ label, helper, errorText, valueE164, onChangeE164, defaultCountry = "IN", placeholder = "Phone number", testID, disabled, onSubmitEditing, showValidationChecked = true }) {
  const [country, setCountry] = useState(() => (valueE164 && detectCountry(valueE164)) || defaultCountry);
  const [national, setNational] = useState(() => (valueE164 ? splitE164(valueE164).national : ""));
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);

  // Reset when parent clears the value
  useEffect(() => { if (!valueE164) setNational((n) => (n && !toE164(country, n) ? n : "")); }, [valueE164]); // eslint-disable-line

  const entry = findCountry(country);
  const valid = isValidForCountry(country, national);

  const emit = (c, n) => {
    const e = toE164(c, n);
    if (e) onChangeE164?.(e, true);
    else {
      const digits = n.replace(/[^0-9]/g, "");
      const dial = findCountry(c)?.dialCode || "";
      onChangeE164?.(digits ? `+${dial}${digits}` : "", false);
    }
  };

  const onType = (t) => {
    const cleaned = t.replace(/[^0-9 ()-]/g, "");
    const formatted = cleaned.length < national.length ? cleaned : formatAsYouType(country, cleaned);
    setNational(formatted);
    emit(country, formatted);
  };

  const list = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^\+/, "");
    const all = listCountries();
    if (!s) return all;
    return all.filter((c) => c.name.toLowerCase().includes(s) || c.dialCode.startsWith(s) || c.iso2.toLowerCase() === s);
  }, [q]);

  return (
    <div className="flex w-full flex-col gap-1.5">
      {label ? <label className="text-xs font-semibold uppercase tracking-[0.4px] text-a-muted">{label}</label> : null}
      <div className={`flex min-h-[48px] items-stretch rounded-xl border bg-a-surface transition-colors ${errorText ? "border-a-danger" : focused ? "border-a-primary" : "border-a-border"}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          data-testid={testID ? `${testID}-country` : undefined}
          className="flex h-full items-center gap-1.5 self-stretch border-r border-a-border px-3 text-a-text hover:bg-a-text/5"
        >
          <span className="text-lg leading-none">{entry?.flag}</span>
          <span className="text-[15px] font-semibold">+{entry?.dialCode}</span>
          <ChevronDown size={14} className="text-a-muted" />
        </button>
        <input
          data-testid={testID}
          type="tel"
          inputMode="tel"
          disabled={disabled}
          value={national}
          placeholder={placeholder}
          onChange={(e) => onType(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={(e) => { if (e.key === "Enter" && onSubmitEditing) { e.preventDefault(); onSubmitEditing(); } }}
          className="min-w-0 flex-1 bg-transparent px-3 py-3 text-[15px] text-a-text outline-none placeholder:text-a-muted"
        />
        {showValidationChecked && national.length > 0 ? (
          <span className="flex items-center pr-3">{valid ? <Check size={18} className="text-a-success" /> : <X size={18} className="text-a-danger" />}</span>
        ) : null}
      </div>
      {errorText ? <p className="text-xs text-a-danger">{errorText}</p> : helper ? <p className="text-xs text-a-muted">{helper}</p> : null}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[80vh] max-w-md gap-0 overflow-hidden border-a-border bg-a-surface p-0 text-a-text">
          <DialogTitle className="border-b border-a-border px-4 py-3.5 text-base font-extrabold">Select country</DialogTitle>
          <div className="p-3">
            <div className="flex items-center gap-2 rounded-xl border border-a-border bg-a-bg px-3">
              <Search size={16} className="text-a-muted" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search country or code (e.g. +91)"
                className="h-11 flex-1 bg-transparent text-sm text-a-text outline-none placeholder:text-a-muted"
                data-testid="country-search"
              />
            </div>
          </div>
          <div className="max-h-[55vh] overflow-y-auto pb-2">
            {list.map((c) => (
              <button
                key={c.iso2}
                type="button"
                onClick={() => { setCountry(c.iso2); setOpen(false); setQ(""); emit(c.iso2, national); }}
                className={`flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-a-dim ${c.iso2 === country ? "bg-a-dim" : ""}`}
                data-testid={`country-option-${c.iso2}`}
              >
                <span className="text-xl">{c.flag}</span>
                <span className="flex-1 text-sm font-semibold text-a-text">{c.name}</span>
                <span className="text-sm text-a-muted">+{c.dialCode}</span>
                {c.iso2 === country ? <Check size={16} className="text-a-primary" /> : null}
              </button>
            ))}
            {list.length === 0 ? <p className="px-4 py-6 text-center text-sm text-a-muted">No countries match.</p> : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
